import React from 'react'
import PropTypes from 'prop-types'
import EmailOutboxRowActions from './EmailOutboxRowActions'
import {
  formatRelativeTime,
  formatTemplateKey,
  getStatusBadgeColor,
  getTemplateBadgeColor,
  getTemplateDescription,
  truncate,
} from './emailOutboxUtils'

function SortableHeader({
  column,
  sortColumn,
  sortDirection,
  onSort,
  children,
  title,
}) {
  return (
    <th
      onClick={() => onSort(column)}
      onKeyDown={(e) => e.key === 'Enter' && onSort(column)}
      scope="col"
      tabIndex={0}
      title={title}
      aria-sort={
        sortColumn === column
          ? sortDirection === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
    >
      {children}{' '}
      {sortColumn === column && (sortDirection === 'asc' ? '↑' : '↓')}
    </th>
  )
}

SortableHeader.propTypes = {
  column: PropTypes.string.isRequired,
  sortColumn: PropTypes.string.isRequired,
  sortDirection: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onSort: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
}

function EmailOutboxTable({
  drafts,
  sortColumn,
  sortDirection,
  onSort,
  onPreview,
  onTestSend,
  onSend,
  onCancel,
  onRetry,
}) {
  if (drafts.length === 0) {
    return (
      <div className="alert alert-info">
        No drafts found. Click "Generate Drafts" to create email drafts for this
        league.
      </div>
    )
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover email-outbox-table">
        <thead>
          <tr>
            <SortableHeader
              column="player_name"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            >
              Recipient
            </SortableHeader>
            <SortableHeader
              column="template_key"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
              title="The type of reminder email being sent to the player"
            >
              Email Type
            </SortableHeader>
            <th>Subject</th>
            <SortableHeader
              column="status"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            >
              Status
            </SortableHeader>
            <SortableHeader
              column="cycle_key"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            >
              Cycle
            </SortableHeader>
            <SortableHeader
              column="generated_at"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            >
              Generated
            </SortableHeader>
            <SortableHeader
              column="sent_at"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            >
              Sent
            </SortableHeader>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {drafts.map((draft) => (
            <tr key={draft.id}>
              <td>
                <strong>{draft.player_name}</strong>
                <div className="text-muted small">{draft.user_email}</div>
              </td>
              <td>
                <span
                  className={`badge bg-${getTemplateBadgeColor(draft.template_key)}`}
                  title={getTemplateDescription(draft.template_key)}
                  style={{ cursor: 'help' }}
                >
                  {formatTemplateKey(draft.template_key)}
                </span>
              </td>
              <td title={draft.subject}>
                <div className="email-subject">
                  {truncate(draft.subject, 50)}
                </div>
                <div className="text-muted small">To: {draft.user_email}</div>
              </td>
              <td>
                <span
                  className={`badge bg-${getStatusBadgeColor(draft.status)}`}
                >
                  {draft.status}
                </span>
              </td>
              <td className="text-muted small">{draft.cycle_key}</td>
              <td>{formatRelativeTime(draft.generated_at)}</td>
              <td>{draft.sent_at ? formatRelativeTime(draft.sent_at) : '—'}</td>
              <td>
                <EmailOutboxRowActions
                  draft={draft}
                  onPreview={onPreview}
                  onTestSend={onTestSend}
                  onSend={onSend}
                  onCancel={onCancel}
                  onRetry={onRetry}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

EmailOutboxTable.propTypes = {
  drafts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      player_name: PropTypes.string,
      user_email: PropTypes.string,
      template_key: PropTypes.string,
      subject: PropTypes.string,
      status: PropTypes.string,
      cycle_key: PropTypes.string,
      generated_at: PropTypes.string,
      sent_at: PropTypes.string,
    }),
  ).isRequired,
  sortColumn: PropTypes.string.isRequired,
  sortDirection: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onSort: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onTestSend: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
}

export default EmailOutboxTable
